/* Compact Mass Messages / Priority Mass Messages (Page redesign: compact
   layout). Two tabs: Setup (campaigns on top, message lists below) and
   Schedule (the queue, by day). The shell owns the data both tabs read —
   campaigns, lists and the queue — so a campaign's "See schedule" count and
   the tab badges agree with what the Schedule tab shows. */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import {
  hasMassCampaignManagementAccess,
  hasMassMessagesCampaignsAccess,
  hasMassMessagesListsAccess,
  hasMassMessagesSchedulesAccess,
  hasPriorityMassCampaignManagementAccess,
  hasPriorityMassMessagesCampaignsAccess,
  hasPriorityMassMessagesListsAccess,
  hasPriorityMassMessagesSchedulesAccess,
  isAccountOwner,
} from '../../../permissions';
import { SubTabBar } from '../../SubTabBar';
import { CopyConfigModal } from '../../CopyConfigModal';
import { CopyCampaignModal } from '../../CopyCampaignModal';
import { font as f } from '../../ChatAssistant/compact/style';
import { C } from '../../ChatAssistant/compact/tokens';
import { massApi, type MsgList } from './massApi';
import { campOf, isQueued, type Camp, type Kind, type Platform, type Row } from './model';
import { SetupPage } from './SetupPage';
import { SchedulePage } from './SchedulePage';

export interface MassData {
  camps: Camp[];
  lists: MsgList[];
  rows: Row[];
  loading: boolean;
  failed: boolean;
  rowsLoading: boolean;
  reload: () => Promise<void>;
  reloadRows: (silent?: boolean) => Promise<void>;
  setCamps: (fn: (c: Camp[]) => Camp[]) => void;
  setLists: (fn: (l: MsgList[]) => MsgList[]) => void;
  setRows: (fn: (r: Row[]) => Row[]) => void;
}

function useMassData(kind: Kind, accountId: string, platform: Platform, opts: { lists: boolean; schedule: boolean }): MassData {
  const a = useMemo(() => massApi(kind, accountId), [kind, accountId]);
  const [camps, setCamps] = useState<Camp[]>([]);
  const [lists, setLists] = useState<MsgList[]>([]);
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [rowsLoading, setRowsLoading] = useState(false);
  const campIds = useRef<number[]>([]);
  const run = useRef(0);

  const reloadRows = useCallback(async (silent = false) => {
    if (!opts.schedule) return;
    const ids = campIds.current;
    const mine = run.current;
    if (!silent) setRowsLoading(true);
    try {
      const all = await Promise.all(ids.map((id) => a.getSchedule(id).catch(() => [] as Row[])));
      if (mine === run.current) setRows(all.flat());
    } finally {
      if (!silent && mine === run.current) setRowsLoading(false);
    }
  }, [a, opts.schedule]);

  const reload = useCallback(async () => {
    const mine = ++run.current;
    try {
      const [cs, ls] = await Promise.all([a.getCampaigns(), opts.lists ? a.getLists() : Promise.resolve([] as MsgList[])]);
      if (mine !== run.current) return;
      const next = cs.map((c) => campOf(c, kind, platform));
      campIds.current = cs.map((c) => c.id);
      setCamps(next);
      setLists(ls);
      setFailed(false);
      setLoading(false);
      await reloadRows();
    } catch (e) {
      console.error('Failed to load mass messages:', e);
      if (mine === run.current) { setFailed(true); setLoading(false); }
    }
  }, [a, kind, platform, opts.lists, reloadRows]);

  useEffect(() => { setLoading(true); void reload(); }, [reload]);

  // Keep the ids the queue is read from in step with campaigns added or removed in place.
  useEffect(() => { campIds.current = camps.map((c) => c.id!); }, [camps]);

  return { camps, lists, rows, loading, failed, rowsLoading, reload, reloadRows, setCamps, setLists, setRows };
}

export function MassMessagesPage({ kind, accountId, platform = 'onlyfans' }: { kind: Kind; accountId: string; platform?: Platform }) {
  const { t } = useTranslation('massCompact');
  const [params, setParams] = useSearchParams();
  const { getPermissions } = useAuth();
  const perms = getPermissions(accountId);
  const owner = isAccountOwner(perms);
  const pri = kind === 'priority';
  const access = {
    lists: owner || (pri ? hasPriorityMassMessagesListsAccess(perms) : hasMassMessagesListsAccess(perms)),
    campaigns: owner || (pri ? hasPriorityMassMessagesCampaignsAccess(perms) : hasMassMessagesCampaignsAccess(perms)),
    schedule: owner || (pri ? hasPriorityMassMessagesSchedulesAccess(perms) : hasMassMessagesSchedulesAccess(perms)),
  };
  const canManage = pri ? hasPriorityMassCampaignManagementAccess(perms) : hasMassCampaignManagementAccess(perms);

  const data = useMassData(kind, accountId, platform, { lists: access.lists || canManage, schedule: access.schedule || access.campaigns });
  const [schedCamp, setSchedCamp] = useState<number | 'all'>('all');
  const [copy, setCopy] = useState<'lists' | 'campaigns' | null>(null);

  // ?msub= / ?psub= — the old Lists and Campaigns tabs both land on Setup.
  const key = pri ? 'psub' : 'msub';
  const raw = params.get(key);
  const tabs = [
    { id: 'setup', visible: access.lists || access.campaigns },
    { id: 'schedule', visible: access.schedule },
  ].filter((x) => x.visible).map((x) => x.id);
  const want = raw === 'schedule' ? 'schedule' : 'setup';
  const tab = tabs.includes(want) ? want : tabs[0] ?? 'setup';
  const go = (id: string) => setParams({ ...Object.fromEntries(params.entries()), [key]: id });

  const pending = data.rows.filter(isQueued).length;
  const badge = (n: number, on: boolean) => <span style={{ font: f(600, 10.5), color: on ? C.purpleLt : C.sub, background: C.input, padding: '1px 6px', borderRadius: 99, fontVariantNumeric: 'tabular-nums' }}>{n}</span>;
  const setupCount = (access.lists ? data.lists.length : 0) + (access.campaigns ? data.camps.length : 0);

  return (
    <div className="cpt-root">
      <SubTabBar
        tabs={[
          ...(tabs.includes('setup') ? [{ id: 'setup', label: t('tab.setup'), icon: 'settings', badge: data.loading ? undefined : badge(setupCount, tab === 'setup') }] : []),
          ...(tabs.includes('schedule') ? [{ id: 'schedule', label: t('tab.schedule'), icon: 'calendar', badge: data.loading ? undefined : badge(pending, tab === 'schedule') }] : []),
        ]}
        active={tab}
        onChange={go}
      />

      {tab === 'setup' && (
        <SetupPage
          kind={kind}
          accountId={accountId}
          platform={platform}
          data={data}
          access={access}
          canManage={canManage}
          onSeeSchedule={access.schedule ? (id) => { setSchedCamp(id); go('schedule'); } : undefined}
          onCopyLists={() => setCopy('lists')}
          onCopyCampaigns={() => setCopy('campaigns')}
        />
      )}
      {tab === 'schedule' && (
        <SchedulePage
          kind={kind}
          accountId={accountId}
          data={data}
          camp={schedCamp}
          setCamp={setSchedCamp}
          onOpenSetup={tabs.includes('setup') ? () => go('setup') : undefined}
        />
      )}

      <CopyConfigModal
        type={pri ? 'priority' : 'mass'}
        currentAccountId={accountId}
        isOpen={copy === 'lists'}
        onClose={() => setCopy(null)}
        onComplete={() => { void data.reload(); }}
      />
      <CopyCampaignModal
        type={pri ? 'priority' : 'mass'}
        currentAccountId={accountId}
        isOpen={copy === 'campaigns'}
        onClose={() => setCopy(null)}
        onComplete={() => { void data.reload(); }}
      />
    </div>
  );
}
